import { LOGINS_STORE } from "@/constants/sessionStorage";
import { rawDefaultStateValuesGetter, zustandStorage } from "@/lib/storage";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const loginsStoreSchema = z.object({
  username: z.string().optional(),
  owner: z.int().optional(),
  location: z.string().optional(),
  active: z.boolean().optional(),
  minCreatedAt: z.string().optional(),
  maxCreatedAt: z.string().optional(),
  showHidden: z.boolean().optional(),

  sortBy: z.string().optional(),
  orderBy: z.string().optional(),
  page: z.number().min(1),
  pageSize: z.number().max(200),
});

export type LoginsStore = z.infer<typeof loginsStoreSchema>;

const defaults: LoginsStore = {
  page: 1,
  pageSize: 20,
};

interface Store {
  state: LoginsStore;
  setState: (newState: Partial<LoginsStore>) => void;
  reset: () => void;
}

const getRawDefaultValues = rawDefaultStateValuesGetter(LOGINS_STORE);

const { data: defaultValues } = loginsStoreSchema.safeParse(getRawDefaultValues());

const useStore = create<Store>()(
  persist(
    (set) => ({
      state: defaultValues ?? defaults,

      setState: (newState) =>
        set((s) => {
          const merged = { ...s.state, ...newState };
          const parsed = loginsStoreSchema.safeParse(merged);
          return {
            state: parsed.success ? parsed.data : s.state,
          };
        }),

      reset: () => set({ state: defaults }),
    }),
    {
      name: LOGINS_STORE,
      onRehydrateStorage: () => (state) => {
        if (!state?.state) return;

        const parsed = loginsStoreSchema.safeParse(state.state);

        if (!parsed.success) {
          state.state = defaults;
          return;
        }

        state.state = parsed.data;
      },
      partialize: (state) => state.state,
      storage: zustandStorage,
    },
  ),
);

export const useLoginsStore = () => {
  const store = useStore();
  const [params] = useSearchParams();
  const paramsValue = params.get(LOGINS_STORE);

  useEffect(() => {
    if (!paramsValue) {
      store.reset();
    }
  }, [paramsValue]);

  return store;
};
